<template>
    <div class="rental-form-video-uploader property-edit-video-uploader">
        <input
            ref="picker"
            type="file"
            class="property-edit-video-input"
            accept="video/mp4,video/quicktime,.mp4,.mov"
            style="display:none"
            @change="onFilePicked"
        />

        <landlord-media-batch-progress
            v-if="uploadSessionIds.length > 0 || bulkRemoveInProgress"
            :title="progressTitleResolved"
            :done="videoStats.done"
            :total="videoStats.total"
            :failed="videoStats.failed"
            :pending="videoStats.pending"
            :bytes-loaded="videoStats.loadedBytes"
            :bytes-total="videoStats.totalBytes"
            :mode="bulkRemoveInProgress ? 'delete' : (videoStats.pending > 0 && videoStats.done < videoStats.total ? 'processing' : 'upload')"
            :status="progressStatus"
            :indeterminate="bulkRemoveInProgress"
            :lang="resolvedLang"
        ></landlord-media-batch-progress>

        <div v-if="tiles.length > 0 || bulkRemoveInProgress" class="property-edit-media-toolbar">
            <span class="property-edit-media-toolbar__status">
                <template v-if="bulkRemoveInProgress">{{ i18n.removingLabel || '...' }}</template>
                <template v-else>{{ tiles.length }} {{ i18n.videosCountLabel || '' }}</template>
            </span>
            <button type="button"
                    class="property-edit-media-toolbar__icon-btn"
                    :disabled="bulkRemoveInProgress"
                    :aria-label="i18n.removeAllLabel"
                    :title="i18n.removeAllLabel"
                    @click.prevent="bulkRemove"><i class="icon-delete" aria-hidden="true"></i></button>
        </div>

        <div class="theme-gallery">
            <draggable
                tag="ul"
                class="theme-gallery__list"
                v-model="tiles"
                :draggable="'.theme-gallery__item--draggable'"
                v-bind="dragOpts"
                @end="onDragEnd"
            >
                <li
                    v-for="(tile, index) in tiles"
                    :key="tile.recordId"
                    class="theme-gallery__item theme-gallery__item--draggable"
                    :title="tile.originalFilename"
                >
                    <div class="theme-gallery__item-inner" style="position: relative;">
                        <img
                            v-if="tile.thumbnailUrl"
                            :src="tile.thumbnailUrl"
                            :alt="tile.originalFilename"
                            class="theme-gallery__thumbnail"
                            style="object-fit: cover;"
                        />
                        <div
                            v-else
                            class="theme-gallery__thumbnail"
                            style="display: flex; align-items: center; justify-content: center; text-align: center;"
                        >
                            <i class="icon-file" style="font-size: 28px; display:block;"></i>
                        </div>
                        <button
                            type="button"
                            class="theme-gallery__close"
                            :aria-label="i18n.removeLabel"
                            :title="i18n.removeLabel"
                            style="z-index: 2; font-weight: bold; line-height: 1;"
                            @click.prevent.stop="removeTile(tile)"
                        ><span aria-hidden="true">&times;</span></button>
                        <span
                            v-if="tile.status"
                            class="theme-gallery__status"
                            :class="'theme-gallery__status--' + statusCssState(tile.status)"
                            :title="statusLabel(tile)"
                            style="position: absolute; bottom: 4px; left: 4px; padding: 2px 6px; font-size: 11px; line-height: 1; border-radius: 3px; z-index: 2;"
                        >{{ statusLabel(tile) }}</span>
                        <div
                            v-if="tile.status === 'failed'"
                            class="theme-gallery__failed-overlay"
                            style="position: absolute; inset: 0; background: rgba(0,0,0,0.55); display: flex; align-items: center; justify-content: center; gap: 8px; z-index: 3;"
                        >
                            <button
                                type="button"
                                class="theme-btn theme-btn--small theme-btn--white"
                                :title="i18n.retryLabel"
                                @click.prevent.stop="retryTile(tile)"
                            >{{ i18n.retryLabel }}</button>
                        </div>
                    </div>
                    <div class="theme-gallery__item-index">{{ index + 1 }}</div>
                    <p
                        v-if="tile.status === 'failed' && tile.errorMessage"
                        class="text-xs text-red-700 mt-1"
                        style="max-width: 120px; word-break: break-word;"
                    >{{ tile.errorMessage }}</p>
                </li>
                <li
                    slot="footer"
                    class="theme-gallery__item-to-drop"
                    :title="i18n.addVideoLabel"
                    :aria-label="i18n.addVideoLabel"
                    @click="openPicker"
                >
                    <i class="icon-plus" style="font-size: 20px; margin-bottom: 6px;"></i>
                    <span>{{ i18n.addVideoLabel }}</span>
                </li>
            </draggable>
        </div>

        <div v-if="tiles.length > 0 || bulkRemoveInProgress" class="property-edit-media-toolbar">
            <span class="property-edit-media-toolbar__status">
                <template v-if="bulkRemoveInProgress">{{ i18n.removingLabel || '...' }}</template>
                <template v-else>{{ tiles.length }} {{ i18n.videosCountLabel || '' }}</template>
            </span>
            <button type="button"
                    class="property-edit-media-toolbar__icon-btn"
                    :disabled="bulkRemoveInProgress"
                    :aria-label="i18n.removeAllLabel"
                    :title="i18n.removeAllLabel"
                    @click.prevent="bulkRemove"><i class="icon-delete" aria-hidden="true"></i></button>
        </div>
    </div>
</template>

<script>
import $ from "jquery";
import draggable from "vuedraggable";

const MAX_POLL_ERRORS = 5;

export default {
    props: {
        propertyId: { default: null },
        uploadUrlBase: { type: String, default: '' },
        statusUrlTemplate: { type: String, default: '' },
        deleteUrlTemplate: { type: String, default: '' },
        deleteAllUrlTemplate: { type: String, default: '' },
        orderUrlTemplate: { type: String, default: '' },
        existingVideos: { type: Array, default: function () { return []; } },
        maxSizeMb: { type: Number, default: 100 },
        lang: { type: String, default: 'nl' },
        i18n: {
            type: Object,
            default: function () {
                return {
                    addVideoLabel: '',
                    retryLabel: '',
                    removeLabel: '',
                    statusQueued: '',
                    statusUploading: '',
                    statusProcessing: '',
                    statusReady: '',
                    statusFailed: '',
                    tooLargeTemplate: '',
                    progressTitle: '',
                };
            }
        },
    },
    components: { draggable },
    computed: {
        resolvedLang: function () {
            return this.lang === 'en' ? 'en' : 'nl';
        },
        sessionTiles: function () {
            var ids = this.uploadSessionIds;
            if (!ids || ids.length === 0) return [];
            var set = {};
            for (var i = 0; i < ids.length; i++) set[ids[i]] = true;
            return this.tiles.filter(function (t) { return set[t.recordId]; });
        },
        videoStats: function () {
            var done = 0, failed = 0, pending = 0, loaded = 0, total = 0;
            var session = this.sessionTiles;
            for (var i = 0; i < session.length; i++) {
                var t = session[i];
                if (t.status === 'ready') done++;
                else if (t.status === 'failed') failed++;
                else pending++;
                if (t.totalBytes)  total  += t.totalBytes;
                if (t.loadedBytes) loaded += t.loadedBytes;
            }
            return {
                total: session.length,
                done: done,
                failed: failed,
                pending: pending,
                loadedBytes: loaded,
                totalBytes: total,
            };
        },
        progressTitleResolved: function () {
            if (this.i18n && this.i18n.progressTitle) return this.i18n.progressTitle;
            return this.resolvedLang === 'nl' ? "Video's uploaden" : 'Uploading videos';
        },
        progressStatus: function () {
            var s = this.videoStats;
            if (s.total <= 0) return 'active';
            if (s.pending <= 0 && s.failed > 0) return 'failed';
            if (s.pending <= 0 && s.failed === 0) return 'done';
            return 'active';
        },
    },
    data: function () {
        return {
            tiles: [],
            bulkRemoveInProgress: false,
            nextRecordId: 1,
            uploadInflight: false,
            pollIntervalMs: 2500,
            pollHandles: {},
            // recordIds added by the user in the current session. Rehydrated server-loaded
            // videos are excluded so the progress strip only reflects the local batch.
            uploadSessionIds: [],
            dragOpts: {
                delay: 400,
                delayOnTouchOnly: true,
                touchStartThreshold: 3,
                fallbackTolerance: 3,
            },
        };
    },
    mounted: function () {
        this.rehydrateFromExisting();
    },
    beforeDestroy: function () {
        var keys = Object.keys(this.pollHandles);
        for (var i = 0; i < keys.length; i++) {
            clearInterval(this.pollHandles[keys[i]]);
        }
        this.pollHandles = {};
    },
    methods: {
        emitChanged: function () {
            var snapshot = this.tiles.map(function (t) {
                return { recordId: t.recordId, serverId: t.serverId, status: t.status };
            });
            this.$emit('videos-changed', snapshot);
        },
        onDragEnd: function () {
            this.emitChanged();
            this.persistOrder();
        },
        persistOrder: function () {
            if (!this.propertyId || !this.orderUrlTemplate) return;
            var orderedIds = this.tiles
                .filter(function (t) { return t.status === 'ready' && t.serverId; })
                .map(function (t) { return t.serverId; });
            if (orderedIds.length === 0) return;
            var url = this.orderUrlTemplate.replace('__PROPERTY__', this.propertyId);
            $.ajax({
                url: url,
                method: 'POST',
                data: JSON.stringify({ order: orderedIds }),
                contentType: 'application/json',
                headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content },
            });
        },
        statusCssState: function (status) {
            if (status === 'queued') return 'queued';
            if (status === 'uploading') return 'uploading';
            if (status === 'processing') return 'uploading';
            if (status === 'ready') return 'uploaded';
            if (status === 'failed') return 'failed';
            return 'queued';
        },
        statusLabel: function (tile) {
            if (tile.status === 'queued') return this.i18n.statusQueued || '';
            if (tile.status === 'uploading') {
                var base = this.i18n.statusUploading || '';
                return tile.progress > 0 ? base + ' ' + tile.progress + '%' : base;
            }
            if (tile.status === 'processing') return this.i18n.statusProcessing || '';
            if (tile.status === 'ready') return this.i18n.statusReady || '';
            if (tile.status === 'failed') return this.i18n.statusFailed || '';
            return '';
        },
        rehydrateFromExisting: function () {
            var that = this;
            if (!Array.isArray(this.existingVideos) || this.existingVideos.length === 0) return;
            this.existingVideos.forEach(function (v) {
                var status = v.processing_status === 'ready' ? 'ready'
                    : (v.processing_status === 'failed' ? 'failed' : 'processing');
                var tile = {
                    recordId:         that.nextRecordId++,
                    serverId:         v.id,
                    originalFilename: v.original_filename || '',
                    status:           status,
                    progress:         0,
                    thumbnailUrl:     v.thumbnail_url || null,
                    url:              v.url || null,
                    errorMessage:     status === 'failed' ? that.i18n.statusFailed : null,
                    errorCode:        v.failure_reason || null,
                    pollErrors:       0,
                    file:             null,
                    xhr:              null,
                    removed:          false,
                    loadedBytes:      0,
                    totalBytes:       0,
                };
                that.tiles.push(tile);
                if (tile.status === 'processing') {
                    that.startPolling(tile);
                }
            });
            this.emitChanged();
        },
        openPicker: function () {
            this.$refs.picker.value = null;
            this.$refs.picker.click();
        },
        onFilePicked: function (event) {
            var file = event.target.files && event.target.files[0];
            event.target.value = null;
            if (!file) return;

            var sizeMb = Math.ceil(file.size / (1024 * 1024));
            if (file.size > this.maxSizeMb * 1024 * 1024) {
                var tmpl = this.i18n.tooLargeTemplate || '';
                var msg = tmpl
                    .replace(':size', String(sizeMb))
                    .replace(':limit', String(this.maxSizeMb));
                var failedTile = {
                    recordId:         this.nextRecordId++,
                    serverId:         null,
                    originalFilename: file.name,
                    status:           'failed',
                    progress:         0,
                    thumbnailUrl:     null,
                    url:              null,
                    errorMessage:     msg || this.i18n.statusFailed,
                    errorCode:        'video_too_large',
                    pollErrors:       0,
                    file:             null,
                    xhr:              null,
                    removed:          false,
                    loadedBytes:      0,
                    totalBytes:       0,
                };
                this.tiles.push(failedTile);
                this.uploadSessionIds.push(failedTile.recordId);
                this.emitChanged();
                return;
            }

            var tile = {
                recordId:         this.nextRecordId++,
                serverId:         null,
                originalFilename: file.name,
                status:           'queued',
                progress:         0,
                thumbnailUrl:     null,
                url:              null,
                errorMessage:     null,
                errorCode:        null,
                pollErrors:       0,
                file:             file,
                xhr:              null,
                removed:          false,
                loadedBytes:      0,
                totalBytes:       file.size || 0,
            };
            this.tiles.push(tile);
            this.uploadSessionIds.push(tile.recordId);
            this.emitChanged();
            this.pumpQueued();
        },
        pumpQueued: function () {
            if (this.uploadInflight) return;
            if (!this.propertyId) return;
            for (var i = 0; i < this.tiles.length; i++) {
                if (this.tiles[i].status === 'queued' && this.tiles[i].file) {
                    this.startUpload(this.tiles[i]);
                    return;
                }
            }
        },
        startUpload: function (tile) {
            var that = this;
            tile.status = 'uploading';
            tile.progress = 0;
            this.uploadInflight = true;
            this.emitChanged();

            var url = this.uploadUrlBase.replace('__PROPERTY__', this.propertyId);
            var formData = new FormData();
            formData.append('file', tile.file);

            tile.xhr = $.ajax({
                url: url,
                method: 'POST',
                data: formData,
                contentType: false,
                processData: false,
                dataType: 'json',
                headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content },
                xhr: function () {
                    var x = $.ajaxSettings.xhr();
                    if (x && x.upload) {
                        x.upload.addEventListener('progress', function (e) {
                            if (e.lengthComputable) {
                                tile.progress = Math.min(99, Math.round((e.loaded / e.total) * 100));
                                tile.loadedBytes = e.loaded;
                                if (e.total) tile.totalBytes = e.total;
                            }
                        });
                    }
                    return x;
                },
                success: function (resp) {
                    var newServerId = resp && resp.video ? resp.video.id : null;
                    if (tile.removed) {
                        if (newServerId) {
                            that.serverDeleteVideoId(newServerId);
                        }
                        return;
                    }
                    if (!resp || !resp.video) {
                        tile.status = 'failed';
                        tile.errorCode = (resp && resp.error) || 'upload_failed';
                        tile.errorMessage = that.i18n.statusFailed;
                        tile.file = null;
                        that.emitChanged();
                        return;
                    }
                    tile.serverId = newServerId;
                    tile.status = resp.video.processing_status === 'ready' ? 'ready'
                        : (resp.video.processing_status === 'failed' ? 'failed' : 'processing');
                    tile.thumbnailUrl = resp.video.thumbnail_url || null;
                    tile.url = resp.video.url || null;
                    tile.errorCode = resp.video.processing_status === 'failed'
                        ? (resp.video.failure_reason || 'processing_failed')
                        : null;
                    tile.errorMessage = tile.status === 'failed' ? that.i18n.statusFailed : null;
                    tile.file = null;
                    that.emitChanged();
                    if (tile.status === 'processing') {
                        that.startPolling(tile);
                    } else if (tile.status === 'ready') {
                        that.persistOrder();
                    }
                },
                error: function (xhr) {
                    if (tile.removed) return;
                    tile.status = 'failed';
                    tile.file = null;
                    var body = xhr && xhr.responseJSON;
                    if (body && body.code === 'video_too_large') {
                        tile.errorCode = 'video_too_large';
                        var tmpl = that.i18n.tooLargeTemplate || '';
                        tile.errorMessage = tmpl
                            .replace(':size', String(body.size_mb || ''))
                            .replace(':limit', String(body.limit_mb || that.maxSizeMb));
                    } else {
                        tile.errorCode = (body && body.error) || 'upload_failed';
                        tile.errorMessage = that.i18n.statusFailed;
                    }
                    that.emitChanged();
                },
                complete: function () {
                    tile.xhr = null;
                    that.uploadInflight = false;
                    that.pumpQueued();
                },
            });
        },
        startPolling: function (tile) {
            var that = this;
            if (!tile.serverId || !this.propertyId) return;
            this.stopPolling(tile.recordId);
            tile.pollErrors = 0;
            var url = this.statusUrlTemplate
                .replace('__PROPERTY__', this.propertyId)
                .replace('__VIDEO__', tile.serverId);
            var handle = setInterval(function () {
                if (tile.removed) {
                    that.stopPolling(tile.recordId);
                    return;
                }
                $.ajax({
                    url: url,
                    method: 'GET',
                    dataType: 'json',
                    success: function (resp) {
                        if (tile.removed) {
                            that.stopPolling(tile.recordId);
                            return;
                        }
                        if (!resp || !resp.video) {
                            tile.pollErrors++;
                            if (tile.pollErrors >= MAX_POLL_ERRORS) {
                                tile.status = 'failed';
                                tile.errorCode = 'status_unavailable';
                                tile.errorMessage = that.i18n.statusFailed;
                                that.stopPolling(tile.recordId);
                                that.emitChanged();
                            }
                            return;
                        }
                        tile.pollErrors = 0;
                        var s = resp.video.processing_status;
                        if (s === 'ready') {
                            tile.status = 'ready';
                            tile.thumbnailUrl = resp.video.thumbnail_url || null;
                            tile.url = resp.video.url || null;
                            tile.errorMessage = null;
                            tile.errorCode = null;
                            that.stopPolling(tile.recordId);
                            that.emitChanged();
                            that.persistOrder();
                        } else if (s === 'failed') {
                            tile.status = 'failed';
                            tile.errorCode = resp.video.failure_reason || 'processing_failed';
                            tile.errorMessage = that.i18n.statusFailed;
                            that.stopPolling(tile.recordId);
                            that.emitChanged();
                        }
                    },
                    error: function () {
                        if (tile.removed) {
                            that.stopPolling(tile.recordId);
                            return;
                        }
                        tile.pollErrors++;
                        if (tile.pollErrors >= MAX_POLL_ERRORS) {
                            tile.status = 'failed';
                            tile.errorCode = 'status_unavailable';
                            tile.errorMessage = that.i18n.statusFailed;
                            that.stopPolling(tile.recordId);
                            that.emitChanged();
                        }
                    },
                });
            }, this.pollIntervalMs);
            this.pollHandles[tile.recordId] = handle;
        },
        stopPolling: function (recordId) {
            if (this.pollHandles[recordId]) {
                clearInterval(this.pollHandles[recordId]);
                delete this.pollHandles[recordId];
            }
        },
        serverDeleteVideoId: function (serverId) {
            if (!serverId || !this.propertyId || !this.deleteUrlTemplate) return;
            var url = this.deleteUrlTemplate
                .replace('__PROPERTY__', this.propertyId)
                .replace('__VIDEO__', serverId);
            $.ajax({
                url: url,
                method: 'DELETE',
                headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content },
            });
        },
        retryTile: function (tile) {
            this.stopPolling(tile.recordId);
            if (tile.xhr && typeof tile.xhr.abort === 'function') {
                try { tile.xhr.abort(); } catch (e) {}
            }
            tile.removed = true;
            if (tile.serverId) {
                this.serverDeleteVideoId(tile.serverId);
            }
            var idx = this.tiles.indexOf(tile);
            if (idx >= 0) this.tiles.splice(idx, 1);
            this.emitChanged();
            this.openPicker();
        },
        removeTile: function (tile) {
            var that = this;
            var confirmText = (this.i18n && this.i18n.removeConfirmText) || '';
            var cancelLabel = (this.i18n && this.i18n.removeConfirmCancel) || 'Cancel';
            var okLabel = (this.i18n && this.i18n.removeConfirmOk) || 'Ok';
            var hasSweet = typeof window !== 'undefined' && window.app && typeof window.app.sweetalert === 'function';
            var runDelete = function () { that.removeTileConfirmed(tile); };
            if (hasSweet && confirmText) {
                window.app.sweetalert(confirmText, {
                    buttons: [cancelLabel, okLabel],
                    dangerMode: true,
                }).then(function (value) {
                    if (value) runDelete();
                });
                return;
            }
            if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
                if (window.confirm(confirmText || 'Delete?')) runDelete();
                return;
            }
            runDelete();
        },
        removeTileConfirmed: function (tile) {
            tile.removed = true;
            this.stopPolling(tile.recordId);
            if (tile.xhr && typeof tile.xhr.abort === 'function') {
                try { tile.xhr.abort(); } catch (e) {}
            }
            if (tile.serverId) {
                this.serverDeleteVideoId(tile.serverId);
            }
            var idx = this.tiles.indexOf(tile);
            if (idx >= 0) this.tiles.splice(idx, 1);
            var sessionIdx = this.uploadSessionIds.indexOf(tile.recordId);
            if (sessionIdx >= 0) this.uploadSessionIds.splice(sessionIdx, 1);
            this.emitChanged();
        },
        getReadyVideoIds: function () {
            return this.tiles
                .filter(function (t) { return t.status === 'ready' && t.serverId; })
                .map(function (t) { return t.serverId; });
        },
        hasActivity: function () {
            return this.tiles.length > 0;
        },
        bulkRemove: function () {
            var that = this;
            if (this.bulkRemoveInProgress) return;
            var count = this.tiles.length;
            var titleStr = (this.i18n && this.i18n.removeAllConfirmText) || (this.i18n && this.i18n.removeAllLabel) || '';
            if (titleStr && count > 0) {
                titleStr = titleStr + ' (' + count + ')';
            }
            var bodyTmpl = (this.i18n && this.i18n.removeAllConfirmBody) || '';
            var bodyStr = bodyTmpl ? bodyTmpl.replace(':count', String(count)) : '';
            var hasSweet = typeof window !== 'undefined' && window.app && typeof window.app.sweetalert === 'function';
            var runDelete = function () {
                that.bulkRemoveInProgress = true;
                that.removeAllOnServer(
                    function () {
                        that.bulkRemoveInProgress = false;
                    },
                    function () {
                        that.bulkRemoveInProgress = false;
                        if (hasSweet) {
                            var errMsg = (that.i18n && that.i18n.genericError) || 'Error';
                            window.app.sweetalert('Error', errMsg, 'error');
                        }
                    }
                );
            };
            if (hasSweet) {
                window.app.sweetalert({
                    title: titleStr,
                    text: bodyStr,
                    buttons: [
                        (this.i18n && this.i18n.removeAllConfirmCancel) || 'Cancel',
                        (this.i18n && this.i18n.removeAllConfirmOk) || 'Ok',
                    ],
                    dangerMode: true,
                }).then(function (value) {
                    if (value) runDelete();
                });
                return;
            }
            if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
                if (window.confirm(titleStr + (bodyStr ? '\n' + bodyStr : ''))) runDelete();
            }
        },
        removeAllOnServer: function (onSuccess, onError) {
            var that = this;
            this.tiles.forEach(function (t) {
                t.removed = true;
                that.stopPolling(t.recordId);
                if (t.xhr && typeof t.xhr.abort === 'function') {
                    try { t.xhr.abort(); } catch (e) {}
                }
            });
            var hasServerBacked = this.tiles.some(function (t) { return !!t.serverId; });
            if (!hasServerBacked || !this.propertyId || !this.deleteAllUrlTemplate) {
                this.tiles = [];
                this.uploadSessionIds = [];
                this.emitChanged();
                if (typeof onSuccess === 'function') onSuccess();
                return;
            }
            var url = this.deleteAllUrlTemplate.replace('__PROPERTY__', this.propertyId);
            $.ajax({
                url: url,
                method: 'DELETE',
                headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content },
                success: function () {
                    that.tiles = [];
                    that.uploadSessionIds = [];
                    that.emitChanged();
                    if (typeof onSuccess === 'function') onSuccess();
                },
                error: function () {
                    if (typeof onError === 'function') onError();
                },
            });
        },
    },
};
</script>
